import { useState, useCallback } from "react";
import {
  normalizeDecimalInput,
  parseSumExpressionNullable,
  formatDecimal,
  roundDecimal,
} from "../utils/decimalUtils";

export function useNumericCell(
  flowValue: number | null | undefined,
  flowId: string,
  field: string,
  onFlowUpdate: (flowId: string, updates: Record<string, unknown>) => void,
  /** Fires on every keystroke with the parsed numeric value (no API call). */
  onParsedChange?: (value: number | null) => void,
) {
  const [localValue, setLocalValue] = useState<string>(
    formatDecimal(flowValue),
  );

  // Sync from prop in the same render cycle (no useEffect delay). The
  // "previous prop" guard MUST be state, not a ref: under React's concurrent
  // rendering a render can be discarded and replayed, but a ref mutation is
  // NOT rolled back with it. A ref guard would advance to the new prop in a
  // discarded render while the paired `setLocalValue` was dropped — the guard
  // then reads equal on the next render and never re-syncs, freezing the
  // input at a stale value (e.g. an AI-accepted quantity that reached the
  // footer total but not this cell — STK-956). State is rolled back and
  // committed atomically with `localValue`, so the two never diverge.
  const [prevFlowValue, setPrevFlowValue] = useState(flowValue);
  if (flowValue !== prevFlowValue) {
    setPrevFlowValue(flowValue);
    setLocalValue(formatDecimal(flowValue));
  }

  const handleChange = useCallback(
    (value: string) => {
      const normalized = normalizeDecimalInput(value);
      setLocalValue(normalized);
      onParsedChange?.(parseSumExpressionNullable(normalized));
    },
    [onParsedChange],
  );

  const handleBlur = useCallback(() => {
    const result = roundDecimal(parseSumExpressionNullable(localValue) ?? 0);
    if (result !== roundDecimal(flowValue ?? 0)) {
      onFlowUpdate(flowId, { [field]: result });
    }
  }, [onFlowUpdate, flowId, field, flowValue, localValue]);

  return { localValue, handleChange, handleBlur };
}
